import { FunctionComponent, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { TypeAnimation } from "react-type-animation";

import { CircularProgress } from "@mui/material";

import { CustomColumnTypes } from "@/enum/list.enum";
import { classNames } from "@/utils/classNames";
import { runOrToast } from "@/utils/runOrToast";

import { useUpdateCostumColumnMutation } from "@/pages/Private/redux/list/list.api";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { ListPerson, ListEvent } from "../../schema/list";
import { ColumnValue } from "../../schema/columnValue";
import { ColumnConfig } from "../../schema/columnConfig";

export interface CustomTextColumnProps {
	showEdit?: { id: number; name: string; columnConfigId?: number };
	columnValue?: ColumnValue;
	columnConfig: ColumnConfig;
	row: ListPerson;
	customColumnTextValue?: string;
	setCustomColumnTextValue: (value: string | undefined) => void;
	setShowEdit: (value: { id: number; name: string; columnConfigId?: number }) => void;
	type: CustomColumnTypes;
	currentEvent?: ListEvent;
}

export const CustomTextColumn: FunctionComponent<CustomTextColumnProps> = ({
	showEdit,
	columnValue,
	columnConfig,
	row,
	customColumnTextValue,
	setCustomColumnTextValue,
	setShowEdit,
	type,
	currentEvent,
}) => {
	const [updatePerson, { isLoading }] = useUpdateCostumColumnMutation();
	const { t } = useTranslation();

	if (isLoading) {
		return <CircularProgress />;
	}

	if (showEdit?.id === row.id && showEdit?.columnConfigId === columnConfig?.id) {
		return (
			<textarea
				autoFocus
				className={classNames(
					"sm:shadow-mb-card sm:border-none",
					"input-styles",
					"placeholder-placeholder",
					"text-ssm font-normal",
					"py-3 px-4 w-full"
				)}
				name={columnConfig.name}
				rows={1}
				style={{
					resize: "none",
					overflow: "hidden",
					minHeight: "auto",
					minWidth: "150px",
				}}
				value={customColumnTextValue || ""}
				onBlur={() => setShowEdit({ id: 0, name: "", columnConfigId: 0 })}
				onChange={(event) => setCustomColumnTextValue(event.target.value)}
				onClick={(e: MouseEvent) => {
					e.stopPropagation();
					e.preventDefault();
				}}
				onFocus={(e: React.FormEvent<HTMLTextAreaElement>) => {
					const target = e.target as HTMLTextAreaElement;

					target.style.height = "auto";
					target.style.height = `${target.scrollHeight}px`;

					const length = target.value.length;

					target.setSelectionRange(length, length);
				}}
				onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
					const target = e.target as HTMLTextAreaElement;

					target.style.height = "auto";
					target.style.height = `${target.scrollHeight}px`;
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter" && !e.shiftKey && customColumnTextValue) {
						e.preventDefault();
						const submitted = customColumnTextValue;

						// Close this cell before the save settles: `showEdit` and the draft are shared
						// by every cell, so clearing them after the await would close a cell the user
						// opened meanwhile (PREC-4840).
						setCustomColumnTextValue(undefined);
						setShowEdit({ id: 0, name: "", columnConfigId: 0 });
						void runOrToast(
							() =>
								updatePerson({
									id: row.id,
									columnId: columnConfig.id,
									customColumnName: columnConfig.name,
									value: submitted,
									customColumnType: type,
								}).unwrap(),
							t("errors.saveFailed")
						);
					}
				}}
			/>
		);
	} else {
		let cVal = columnValue?.value ? (columnValue.value as string) : "";

		if (isLoading) {
			cVal = customColumnTextValue || cVal;
		}

		return (
			<Tooltip
				content={
					<div className="flex flex-col min-w-[300px] text-sm">
						<div>
							<span
								className="leading-6 truncate ..."
								style={{
									textWrap: "wrap",
								}}
							>
								{cVal}
							</span>
						</div>
					</div>
				}
				disabled={!cVal}
			>
				<button
					className="w-full"
					onClick={(e: MouseEvent) => {
						e.stopPropagation();
						e.preventDefault();
						setCustomColumnTextValue(columnValue?.value ? (columnValue.value as string) : "");

						setShowEdit({
							id: row.id,
							name: columnConfig.name,
							columnConfigId: columnConfig?.id || 0,
						});
					}}
				>
					{
						<div
							className={classNames(
								"h-[26px] min-w-[150px] cursor-pointer hover:bg-brand-light hover:opacity-[0.7] leading-tight hover:text-white rounded p-1",
								"text-left  w-full",
								"cursor-pointer mr-1  max-w-[200px] text-ellipsis overflow-hidden"
							)}
						>
							{cVal}
							{!cVal && !!currentEvent && (
								<span className="text-xs text-gray-500">
									<TypeAnimation
										repeat={Infinity}
										sequence={[
											"Pending",
											1000,
											"Pending.",
											1000,
											"Pending..",
											1000,
											"Pending...",
											1000,
											"Pending....",
											1000,
										]}
										speed={50}
										wrapper="span"
									/>
								</span>
							)}
						</div>
					}
				</button>
			</Tooltip>
		);
	}
};
